/**
 * Bring a form's first problem into view after a blocked or failed submit.
 *
 * On a phone the submit button sits far below the error summary and the
 * fields, so a failure is otherwise invisible: the button simply stops
 * spinning. This scrolls the first problem — a field still needed (the yellow
 * tint, `data-highlight="required"`), a submit-error alert marked with
 * `data-form-problem`, any `role="alert"`, or a control with
 * `aria-invalid="true"` / `data-invalid`, whichever comes first in the
 * document — to the middle of the screen, moves focus to it (so screen
 * readers land there too) and briefly outlines it (7 Oct: "jump to what's
 * missing"). The outline pulses only when the person has not asked for
 * reduced motion; the scroll is instant then too.
 */

import { firstProblemIndex, type ProblemCandidate } from "./form-checks";

// `data-invalid` is what the design system's (Base UI) `Field` puts on an
// invalid field, so custom controls that do not set `aria-invalid` are found too.
const CANDIDATE_SELECTOR =
  '[data-highlight], [data-form-problem], [role="alert"], [aria-invalid], [data-invalid]';

const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/** How long the outline stays on the field it points at. */
const PULSE_MS = 1800;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function describe(element: HTMLElement): ProblemCandidate {
  return {
    highlight: element.getAttribute("data-highlight"),
    invalid:
      element.getAttribute("aria-invalid") === "true" || element.hasAttribute("data-invalid"),
    problem: element.hasAttribute("data-form-problem") || element.getAttribute("role") === "alert",
    // Nothing laid out: a hidden step or a collapsed section.
    hidden: element.getClientRects().length === 0,
  };
}

function focusTarget(problem: HTMLElement): HTMLElement {
  // A tinted wrapper, an invalid field's wrapper or a custom control may carry
  // the flag; focus the real control inside it when there is one. A form-level
  // alert is focused itself, so its message is what is read out.
  if (problem.matches(FOCUSABLE_SELECTOR)) {
    return problem;
  }
  if (!problem.hasAttribute("data-form-problem") && problem.getAttribute("role") !== "alert") {
    const inner = problem.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (inner !== null) {
      return inner;
    }
  }
  // Not focusable by default: make it programmatically focusable without
  // adding it to the tab order.
  if (!problem.hasAttribute("tabindex")) {
    problem.setAttribute("tabindex", "-1");
  }
  return problem;
}

/**
 * Scroll to, focus and briefly outline one element (a field's tinted wrapper
 * or the field itself). Used for a problem, and for "Edit" on a summary.
 */
export function revealElement(element: HTMLElement): void {
  // Outline the whole field (label and all), not just the input.
  const field = element.closest<HTMLElement>("[data-highlight]") ?? element;
  if (typeof field.scrollIntoView === "function") {
    field.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }
  // The scroll above does the moving; focusing must not jump first.
  focusTarget(element).focus({ preventScroll: true });
  field.setAttribute("data-reveal-pulse", "");
  window.setTimeout(() => field.removeAttribute("data-reveal-pulse"), PULSE_MS);
}

/** Scroll to and focus the first problem inside `container`. Returns whether one was found. */
export function revealFirstProblem(container: HTMLElement | null | undefined): boolean {
  if (container === null || container === undefined) {
    return false;
  }
  const elements = Array.from(container.querySelectorAll<HTMLElement>(CANDIDATE_SELECTOR));
  const problem = elements[firstProblemIndex(elements.map(describe))];
  if (problem === undefined) {
    return false;
  }
  revealElement(problem);
  return true;
}

/** Reveal the field marked `data-field-key="<key>"` inside `container` ("Edit" on a summary). */
export function revealField(container: HTMLElement | null | undefined, key: string): boolean {
  const field = container?.querySelector<HTMLElement>(`[data-field-key="${CSS.escape(key)}"]`);
  if (field === null || field === undefined) {
    return false;
  }
  revealElement(field);
  return true;
}

/** Run `action` on the next frame — after the state update that changed the page has rendered. */
function nextFrame(action: () => void): void {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    action();
    return;
  }
  window.requestAnimationFrame(action);
}

/**
 * Same as {@link revealFirstProblem}, but on the next frame — after the state
 * update that put the error on screen has rendered. `getContainer` is read
 * then, so a ref's `.current` is passed as a function.
 */
export function revealFirstProblemSoon(getContainer: () => HTMLElement | null | undefined): void {
  nextFrame(() => {
    revealFirstProblem(getContainer());
  });
}

/** {@link revealField} on the next frame (once the form is shown again). */
export function revealFieldSoon(
  getContainer: () => HTMLElement | null | undefined,
  key: string,
): void {
  nextFrame(() => {
    revealField(getContainer(), key);
  });
}
